import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import { formatMoney } from "../../utils/formatMoney";

interface RouteFixedFare {
  id: string;
  offerId: number | null;
  offerName: string | null;
  vehicleType: string | null;
  fromLocation: string | null;
  fromPostCode: string | null;
  toLocation: string | null;
  toPostCode: string | null;
  fare: number | null;
  status: boolean;
  remarks: string | null;
}

interface SortConfig {
  key: keyof RouteFixedFare | '';
  direction: 'asc' | 'desc';
}

export default function RouteFixedFare() {
  const [fares, setFares] = useState<RouteFixedFare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchFares();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  const fetchFares = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/RouteFixedFare");
      const allFares = response.data;
      
      // Filter by search term
      const filteredFares = allFares.filter((fare: RouteFixedFare) => 
        fare.fromLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fare.toLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fare.fromPostCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fare.toPostCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Sort if configured
      if (sortConfig.key !== '') {
        filteredFares.sort((a: RouteFixedFare, b: RouteFixedFare) => {
          const aValue = String(a[sortConfig.key as keyof RouteFixedFare]);
          const bValue = String(b[sortConfig.key as keyof RouteFixedFare]);
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      // Pagination
      const totalItems = filteredFares.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setTotalPages(calculatedTotalPages);
      setFares(filteredFares.slice(startIndex, endIndex));
    } catch (error) {
      setError("Failed to fetch route fixed fares");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fare?')) return;

    try {
      await axiosInstance.delete(`/RouteFixedFare/${id}`);
      fetchFares();
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to delete fare');
    }
  };

  const handleSort = (key: keyof RouteFixedFare) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: keyof RouteFixedFare) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? "rounded-lg bg-brand-500 text-white"
              : "rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="mx-auto px-4 py-8">
      <PageBreadcrumb pageTitle="Route Fixed Fares"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/route-fixed-fares/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add Route Fixed Fare
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="p-4 border-b">
          <Input
            placeholder="Search fares..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="border-b border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('fromLocation')}
                >
                  From Location {renderSortIcon('fromLocation')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('fromPostCode')}
                >
                  From Postcode {renderSortIcon('fromPostCode')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('toLocation')}
                >
                  To Location {renderSortIcon('toLocation')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('toPostCode')}
                >
                  To Postcode {renderSortIcon('toPostCode')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('vehicleType')}
                >
                  Vehicle Type {renderSortIcon('vehicleType')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('fare')}
                >
                  Fare {renderSortIcon('fare')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : fares.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No route fixed fares found
                  </td>
                </tr>
              ) : (
                fares.map((fare) => {
                  const fareLabel =
                    formatMoney(fare.fare) !== "N/A" ? formatMoney(fare.fare) : "-";
                  return (
                  <tr key={fare.id}>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{fare.fromLocation}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{fare.fromPostCode}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{fare.toLocation}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{fare.toPostCode}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{fare.vehicleType}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {fareLabel}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        fare.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {fare.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-end">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                        to={`/route-fixed-fares/${fare.id}/view`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        View
                      </Link>
                      <Link
                        to={`/route-fixed-fares/${fare.id}`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => fare.id && handleDelete(fare.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && fares.length > 0 && (
          <div className="px-6 py-4 border-t">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 